import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle, useCallback } from "react";
import { toast } from "react-toastify";
import SpeechToText from "src/components/SpeechToText";
import { X, Check } from "lucide-react";
import { requestMicPermission } from "src/utils/micPermission";
import { formatAIText } from "src/utils/formatAIText";
import {
    getAssistantImagesForDisplay,
    shouldHideAssistantSourceLine,
} from "src/utils/chatLogSource";
import { askQuestion, getManagerChatSession, appendManagerChatSessionMessages, createManagerChatSession, renameManagerChatSession, API_BASE_URL } from "../../services/api";
import type {
    ChatFeedbackReason,
    ChatFeedbackType,
    ChatLogMessageItem,
    ChatSessionMessage,
} from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { chatHistoryManager } from "../../services/chatHistory";
import { useChatSession } from "../../contexts/ChatSessionContext";
import SopDialog from "../../components/SopDialog";
import ChatFeedback from "../../components/ChatFeedback";
import { LoadingSpinner } from "../../components/LoadingSpinner";

type ChatMessage = {
    id: string;
    // Stable, persisted id for this turn (matches Mongo `messageId`).
    // Only set when the message originated from this client OR was hydrated
    // with a persisted messageId. Legacy hydrated messages leave this undefined,
    // which intentionally hides feedback controls for those turns.
    messageId?: string;
    role: "user" | "assistant";
    question?: string;
    answer?: string;
    images?: string[];
    timestamp: string;
    sopSource?: {
        sopName: string;
        sectionTitle: string;
        fileId: string;
        documentContent?: string;
    };
    sourcedFrom?: {
        documentName?: string;
        sectionName?: string;
    };
    pdfUrl?: string;
};

type MessageFeedbackEntry = {
    feedbackType: ChatFeedbackType;
    reason?: ChatFeedbackReason;
};

export type ManagerChatbotHandle = { startNewChat: () => void, hasMessages: () => boolean, getMessages: () => ChatSessionMessage[] };

type ManagerChatbotProps = { sessionId?: string | null, onMessagesChange?: (count: number) => void, onFirstTurnReady?: (messages: ChatSessionMessage[], newSessionId?: string) => void };

const ManagerChatbot = forwardRef<ManagerChatbotHandle, ManagerChatbotProps>(({ sessionId, onMessagesChange, onFirstTurnReady }, ref) => {
    const { user, loading: authLoading } = useAuth();
    const { triggerRefresh } = useChatSession();
    const [message, setMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isMicOpen, setIsMicOpen] = useState<boolean>(false);
    const [sttTranscript, setSttTranscript] = useState<string>("");
    const [error, setError] = useState<string>("");
    const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
    /** In-memory feedback selections keyed by assistant `messageId` (survives chat tab switches). */
    const [messageFeedbackByMessageId, setMessageFeedbackByMessageId] = useState<
        Record<string, MessageFeedbackEntry>
    >({});
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const listRef = useRef<HTMLDivElement | null>(null);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [isStartingNewChat, setIsStartingNewChat] = useState(false);
    const pendingSaveAfterLoadRef = useRef<boolean>(false);
    const isLoadingRef = useRef<boolean>(false);
    const [sopDialog, setSopDialog] = useState<{
        isOpen: boolean;
        sopName: string;
        sectionTitle: string;
        documentContent?: string;
        pdfUrl?: string;
    }>({
        isOpen: false,
        sopName: '',
        sectionTitle: '',
        documentContent: undefined,
        pdfUrl: undefined
    });

    // Fallback message for off-topic/irrelevant questions to ensure visibility everywhere
    const OFF_TOPIC_MESSAGE = 'I cannot answer this question from the processed documents. Missing: relevant content in the uploaded files that matches your query';

    // Initialize chat history manager with current user
    useEffect(() => {
        if (user) {
            chatHistoryManager.setCurrentUser({
                email: user.email,
                role: user.role,
                full_name: user.full_name,
            });
        }
    }, [user]);

    // Keep a ref in sync with isLoading to avoid re-binding listeners
    useEffect(() => {
        isLoadingRef.current = isLoading;
    }, [isLoading]);

    // Save chat when leaving the page (single path; chatHistoryManager serializes concurrent saves)
    useEffect(() => {
        const flushSessionToAdminLogs = () => {
            const currentSessionId = sessionId || chatHistoryManager.getCurrentSessionId();
            const messageCount = chatLengthRef.current;

            if (!currentSessionId || messageCount === 0) {
                return;
            }
            if (isLoadingRef.current) {
                pendingSaveAfterLoadRef.current = true;
                return;
            }
            void chatHistoryManager.saveCompletedManagerSession(currentSessionId);
        };

        const handleBeforeUnload = () => {
            flushSessionToAdminLogs();
        };

        const handleVisibilityChange = () => {
            if (document.hidden) {
                flushSessionToAdminLogs();
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [sessionId]);

    // Save chat on component unmount (e.g., route change within SPA)
    useEffect(() => {
        return () => {
            const currentSessionId = chatHistoryManager.getCurrentSessionId();
            const messageCount = chatLengthRef.current;
            if (currentSessionId && messageCount > 0) {
                // Save chat on component unmount
                chatHistoryManager.saveCompletedManagerSession(currentSessionId).catch(() => { });
            }
        };
    }, []);

    // Set current session in chat history manager when sessionId changes
    // Defer loading to the guarded loader effect below to avoid overwriting
    // local messages mid-conversation
    useEffect(() => {
        if (!sessionId) return;
        chatHistoryManager.setCurrentSession(sessionId, false); // Don't save previous session
        // Intentionally NOT calling loadSessionData here; the guarded effect below
        // will handle loading when safe (not during active send, and when needed).
    }, [sessionId]);

    // Removed legacy immediate loader; guarded loader below handles fetching safely


    const isDailyLimitError = (err: any): boolean => {
        const msg = String(err?.message || err || "");
        // Only match our specific daily message limit error, not OpenAI quota errors
        return /daily message limit/i.test(msg) && !/insufficient_quota/i.test(msg);
    };

    const getDailyResetTimeText = (): string => {
        const now = new Date();
        const reset = new Date(now);
        reset.setDate(now.getDate() + 1);
        reset.setHours(0, 0, 0, 0);
        const locale = undefined;
        const opts: Intl.DateTimeFormatOptions = {
            weekday: 'short', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit'
        };
        return new Intl.DateTimeFormat(locale, opts).format(reset);
    };

    // Track current chat length to avoid overwriting local state with stale/partial remote data
    const chatLengthRef = useRef(0);
    useEffect(() => { chatLengthRef.current = chatHistory.length; }, [chatHistory.length]);

    // If a save was requested while loading, perform it once loading finishes
    useEffect(() => {
        if (!isLoading && pendingSaveAfterLoadRef.current) {
            const currentSessionId = chatHistoryManager.getCurrentSessionId();
            const messageCount = chatLengthRef.current;
            pendingSaveAfterLoadRef.current = false;
            if (currentSessionId && messageCount > 0) {
                chatHistoryManager.saveCompletedManagerSession(currentSessionId).catch(() => { });
            }
        }
    }, [isLoading]);

    // Load session messages when switching sessions (not after every send)
    useEffect(() => {
        const run = async () => {
            if (authLoading || !user) return;
            if (!sessionId) return; // Never clear chat just because sessionId is absent

            setIsLoadingHistory(true);
            try {
                const res = await getManagerChatSession(sessionId);
                const msgs = (res?.item?.messages || []).map((m: any, idx: number) => {
                    const answer = m.role === 'assistant' ? m.text : undefined;
                    return {
                        id: m.messageId || `${Date.now()}-${idx}`,
                        messageId: m.messageId || undefined,
                        role: m.role as 'user' | 'assistant',
                        question: m.role === 'user' ? m.text : undefined,
                        answer,
                        images: getAssistantImagesForDisplay(answer || '', m.images),
                        timestamp: m.timestamp || new Date().toISOString(),
                    };
                });

                const logMessages: ChatLogMessageItem[] = (res?.item?.messages || []).map((m: any) => {
                    const text = m.text || '';
                    return {
                        role: m.role as 'user' | 'assistant',
                        text,
                        images: getAssistantImagesForDisplay(
                            m.role === 'assistant' ? text : '',
                            m.images,
                        ),
                        timestamp: m.timestamp || new Date().toISOString(),
                        messageId: m.messageId,
                        sopSource: m.sopSource,
                        sourcedFrom: m.sourcedFrom,
                    };
                });
                await chatHistoryManager.syncSessionFromServer(sessionId, logMessages);

                // Guard: if we already have messages and the server has fewer or equal,
                // do not overwrite local chat (prevents mid-conversation clearing)
                if (chatLengthRef.current > 0 && msgs.length <= chatLengthRef.current) {
                    // Skip remote overwrite if local has more messages
                } else {
                    setChatHistory(msgs);
                }
            } catch {
                // Do not clear chat on fetch failure
            } finally {
                setIsLoadingHistory(false);
            }
        };
        run();
    }, [sessionId, authLoading, user]);

    const handleSendMessage = async () => {
        if (!message.trim() || isLoading) return;
        const question = message.trim();
        setMessage("");
        setError("");

        // Create a session if we don't have one yet (first message)
        let currentSessionId = sessionId;
        let shouldNotifyParent = false;
        if (!currentSessionId) {
            try {
                const response = await createManagerChatSession("New Manager Chat");
                currentSessionId = response.item.id;
                shouldNotifyParent = true;
                // Created new session for first message

                // Immediately update the chat history manager with the new session ID
                chatHistoryManager.setCurrentSession(currentSessionId, false);
            } catch (error) {
                // Handle session creation error
                return;
            }
        }

        const userMsgId = `${Date.now()}-u`;
        const userMsg: ChatMessage = {
            id: userMsgId,
            messageId: userMsgId,
            role: "user",
            question,
            timestamp: new Date().toISOString(),
        };
        setChatHistory((prev) => [...prev, userMsg]);

        try {
            setIsLoading(true);
            // If this is the first message in the current session, proactively rename the session
            try {
                if (currentSessionId && chatHistory.length === 0) {
                    const newTitle = question.length > 60 ? question.slice(0, 59) + '…' : question;
                    await renameManagerChatSession(currentSessionId, newTitle);
                }
            } catch {
                // best-effort; ignore errors here
            }
            let data;
            try {
                data = await askQuestion(question);
                // Debug: verify backend fields arrive
                // Debug: verify backend fields arrive

                // Check if the response contains a quota error message
                if (data.answer && isDailyLimitError(data.answer)) {
                    const resetText = getDailyResetTimeText();
                    const limitMsg: ChatMessage = {
                        id: `${Date.now()}-limit`,
                        role: 'assistant',
                        answer: `Daily message limit reached. You can chat again on ${resetText}.`,
                        timestamp: new Date().toISOString(),
                    };
                    setChatHistory((prev) => [...prev, limitMsg]);
                    setError("");
                    return;
                }
            } catch (e: any) {
                if (isDailyLimitError(e)) {
                    const resetText = getDailyResetTimeText();
                    const limitMsg: ChatMessage = {
                        id: `${Date.now()}-limit`,
                        role: 'assistant',
                        answer: `Daily message limit reached. You can chat again on ${resetText}.`,
                        timestamp: new Date().toISOString(),
                    };
                    setChatHistory((prev) => [...prev, limitMsg]);
                    setError("");
                    return;
                }
                throw e; // Re-throw if not a daily limit error
            }

            // Normalize/force a clear assistant response for off-topic/irrelevant questions
            const rawAnswer = typeof data?.answer === 'string' ? data.answer.trim() : '';
            const isOffTopic = !rawAnswer || /cannot answer this question/i.test(rawAnswer) || /missing:\s*relevant/i.test(rawAnswer);
            const finalAnswer = isOffTopic ? OFF_TOPIC_MESSAGE : rawAnswer;

            const computedPdfUrl = data.pdfUrl
                ? `${API_BASE_URL}${data.pdfUrl}`
                : (data.sopSource && data.sopSource.fileId)
                    ? `${API_BASE_URL}/documents/${data.sopSource.fileId}/pdf`
                    : undefined;

            const aiMsgId = `${Date.now()}-a`;
            const aiMsg: ChatMessage = {
                id: aiMsgId,
                messageId: aiMsgId,
                role: "assistant",
                answer: finalAnswer,
                images: getAssistantImagesForDisplay(finalAnswer, data.images),
                timestamp: new Date().toISOString(),
                sopSource: data.sopSource || undefined,
                sourcedFrom: (data.sourcedFrom && (data.sourcedFrom.documentName || data.sourcedFrom.sectionName)) ? data.sourcedFrom : (data.document_used ? { documentName: data.document_used } : undefined),
                pdfUrl: computedPdfUrl,
            };
            setChatHistory((prev) => [...prev, aiMsg]);

            // Store messages in LangChain-style chat history
            const messages: ChatLogMessageItem[] = [
                { role: 'user', text: question, timestamp: userMsg.timestamp, messageId: userMsg.messageId },
                {
                    role: 'assistant',
                    text: aiMsg.answer || OFF_TOPIC_MESSAGE,
                    images: aiMsg.images,
                    timestamp: aiMsg.timestamp,
                    messageId: aiMsg.messageId,
                    sopSource: aiMsg.sopSource,
                    sourcedFrom: aiMsg.sourcedFrom,
                },
            ];

            const chatHistorySessionId = chatHistoryManager.getCurrentSessionId() || currentSessionId || `temp_${Date.now()}`;
            chatHistoryManager.addMessages(chatHistorySessionId, messages);
            if (currentSessionId) {
                appendManagerChatSessionMessages(currentSessionId, messages).then(() => {
                    void chatHistoryManager.saveCompletedManagerSession(currentSessionId);
                    setTimeout(() => {
                        triggerRefresh();
                    }, 500);
                }).catch((e: any) => {
                    if (isDailyLimitError(e)) {
                        const resetText = getDailyResetTimeText();
                        const limitMsg: ChatMessage = {
                            id: `${Date.now()}-limit`,
                            role: 'assistant',
                            answer: `Daily message limit reached. You can chat again on ${resetText}.`,
                            timestamp: new Date().toISOString(),
                        };
                        setChatHistory((prev) => [...prev, limitMsg]);
                        setError("");
                    }
                });
            }
        } catch (e: any) {
            if (isDailyLimitError(e)) {
                const resetText = getDailyResetTimeText();
                const limitMsg: ChatMessage = {
                    id: `${Date.now()}-limit`,
                    role: 'assistant',
                    answer: `Daily message limit reached. You can chat again on ${resetText}.`,
                    timestamp: new Date().toISOString(),
                };
                setChatHistory((prev) => [...prev, limitMsg]);
                setError("");
            } else {
                setError(e?.message || "Failed to get answer. Please try again.");
            }
        } finally {
            setIsLoading(false);

            // Notify parent component about the new session after message processing is complete
            if (shouldNotifyParent && typeof onFirstTurnReady === 'function') {
                onFirstTurnReady([], currentSessionId);
            }
        }
    };

    const handleStartNewChat = useCallback(async () => {
        setIsStartingNewChat(true);
        const startTime = Date.now();

        try {
            // Save current session to admin logs before starting new chat (if there are messages)
            const currentSessionId = sessionId || chatHistoryManager.getCurrentSessionId();
            // Handle start new chat
            if (currentSessionId && chatHistory.length > 0) {
                // Save current conversation before starting new chat
                await chatHistoryManager.saveCompletedManagerSession(currentSessionId);
            }

            // Clear local chat history immediately
            setChatHistory([]);

            // Notify parent component to start new chat (this will clear sessionId)
            if (typeof onFirstTurnReady === 'function') {
                onFirstTurnReady([], undefined);
            }

            // Trigger refresh to update the chat list
            if (typeof triggerRefresh === 'function') {
                triggerRefresh();
            }

            setMessage("");
            setError("");
        } finally {
            // Ensure loader shows for minimum 1.5 seconds
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, 1500 - elapsedTime);

            setTimeout(() => {
                setIsStartingNewChat(false);
            }, remainingTime);
        }
    }, [sessionId, chatHistory.length, onFirstTurnReady, triggerRefresh]);

    const getMessages = useCallback((): ChatSessionMessage[] => {
        return chatHistory.map((m) => ({
            role: m.role,
            text: m.role === 'user' ? (m.question || '') : (m.answer || ''),
            images: m.images,
            timestamp: m.timestamp,
            messageId: m.messageId,
        }));
    }, [chatHistory]);

    useImperativeHandle(
        ref,
        () => ({
            startNewChat: handleStartNewChat,
            hasMessages: () => chatHistory.length > 0,
            getMessages,
        }),
        [handleStartNewChat, getMessages, chatHistory.length]
    );

    useEffect(() => {
        if (onMessagesChange) onMessagesChange(chatHistory.length);
    }, [chatHistory.length, onMessagesChange]);

    useEffect(() => {
        if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollHeight;
        }
    }, [chatHistory, isLoading]);


    const formatTime = (dateString: string): string => {
        return new Date(dateString).toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const handleViewSop = (sopSource: ChatMessage['sopSource'], pdfUrl?: string) => {
        if (sopSource) {
            const effectiveUrl = pdfUrl || (sopSource.fileId ? `${API_BASE_URL}/documents/${sopSource.fileId}/pdf` : undefined);
            setSopDialog({
                isOpen: true,
                sopName: sopSource.sopName,
                sectionTitle: sopSource.sectionTitle,
                documentContent: sopSource.documentContent,
                pdfUrl: effectiveUrl
            });
        }
    };

    const showConversationLayout = chatHistory.length > 0 || isLoading;

    return (
        <div
            className={`manager-chat-container flex-1 min-h-0 min-w-0 ${
                showConversationLayout ? "has-chat-history" : "no-chat-history"
            }`}
        >
            {/* Chatbot Header with Start New Chat Button */}
            <div className="manager-chat-header flex items-center justify-end">

                <button
                    onClick={handleStartNewChat}
                    disabled={isStartingNewChat}
                    className="renegade-btn-primary mini-btn px-3"
                >
                    {isStartingNewChat ? (
                        <LoadingSpinner size="sm" className="text-white" />
                    ) : (
                        <img src="/icons/addIcon.png" alt="add" className="w-5 h-5" />
                    )}
                    <span className="text-sm font-medium">{isStartingNewChat ? 'Starting...' : 'Start New Chat'}</span>
                </button>

            </div>

            {/* Chat Area */}
            <div className="manager-chat-body">
                {error && (
                    <div className="mb-3 text-sm text-red-600 flex-shrink-0">{error}</div>
                )}

                {chatHistory.length === 0 && !isLoadingHistory ? (
                    <div className="chat-empty-layout">
                        <div className="chat-empty-hero text-center text-gray-600">
                            <h1 className="grey-800">Ask about tools, safety <br />steps, or checklists.</h1>
                        </div>
                        <div className="ai-chat-input-container">
                                {!isMicOpen ? (
                                    <textarea
                                        rows={1}
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        placeholder="Ask about procedures, safety protocols, or SOP guidance...."
                                        className="ai-chat-input"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                handleSendMessage();
                                            }
                                        }}
                                        disabled={isLoading || isLoadingHistory}
                                        style={{
                                            resize: 'none',
                                            overflow: 'auto',
                                            height: '45px',
                                            minHeight: '45px',
                                            maxHeight: '65px'
                                        }}
                                        onInput={(e) => {
                                            const target = e.target as HTMLTextAreaElement;
                                            target.style.height = '45px';
                                            target.style.height = Math.min(target.scrollHeight, 90) + 'px';
                                        }}
                                    ></textarea>
                                ) : (
                                    <div className="ai-chat-mic-pill">
                                        <SpeechToText
                                            className="min-w-0 flex-1 h-full flex items-center"
                                            active={true}
                                            height={29}
                                            windowSec={2}
                                            timesliceMs={40}
                                            onTranscript={(text) => {
                                                setSttTranscript(text);
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setIsMicOpen(false);
                                                setSttTranscript("");
                                            }}
                                            className="flex-shrink-0 ml-2 p-1 rounded-full hover:bg-gray-100 text-gray-700"
                                            aria-label="Cancel voice input"
                                        >
                                            <X className="w-5 h-5" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                if (sttTranscript.trim()) {
                                                    setMessage((prev) => (prev ? prev + ' ' : '') + sttTranscript.trim());
                                                }
                                                setSttTranscript("");
                                                setIsMicOpen(false);
                                            }}
                                            className="flex-shrink-0 ml-1 p-1 rounded-full hover:bg-gray-100 text-gray-700"
                                            aria-label="Accept voice input"
                                        >
                                            <Check className="w-5 h-5" />
                                        </button>
                                    </div>
                                )}
                                {!isMicOpen && (
                                    <button
                                        type="button"
                                        onClick={async () => {
                                            if (isLoading || isLoadingHistory) return;
                                            const result = await requestMicPermission();
                                            if (!result.granted) {
                                                toast.error(result.errorMessage);
                                                return;
                                            }
                                            toast.success("Microphone is active. You can start speaking.");
                                            setIsMicOpen(true);
                                        }}
                                        className="chat-mic-btn"
                                        disabled={isLoading || isLoadingHistory}
                                        title="Click to use voice input"
                                    >
                                        <i className="iconoir-microphone-solid"></i>
                                    </button>
                                )}
                                <button
                                    onClick={handleSendMessage}
                                    className="chat-send-btn"
                                    disabled={isLoading || isLoadingHistory}
                                >
                                    <i className="iconoir-send-solid"></i>
                                </button>
                            </div>
                    </div>
                ) : isLoadingHistory ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center text-gray-600">
                        <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-500"></div>
                        </div>
                        <h3 className="text-xl font-semibold mb-1">Loading chat history...</h3>
                        <p className="text-sm">Please wait while we fetch your previous conversations.</p>
                    </div>
                ) : (
                    <>
                    <div ref={listRef} className="manager-chat-history">
                        {chatHistory.map((m) => {
                            const displayImages =
                                m.role === 'assistant'
                                    ? getAssistantImagesForDisplay(m.answer || '', m.images)
                                    : [];

                            return (
                            <div key={m.id} className="flex flex-col gap-2">
                                {m.role === "user" ? (
                                    <div className="self-end max-w-[80%]">
                                        <div className="manager-sent-msg">
                                            {m.question}
                                        </div>
                                        <div className="text-13-reg text-right grey-700 mt-2 text-capitalize">{user?.role ? ` ${user.role}` : ""} [{formatTime(m.timestamp)}]</div>
                                    </div>
                                ) : (
                                    <div className="self-start">
                                        <div className="manager-rcvd-msg">
                                            <div>
                                                {formatAIText(m.answer || "")}
                                            </div>
                                            {displayImages.length > 0 && (
                                                <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
                                                    {displayImages.map((img, idx) => (
                                                        <img key={idx} src={img} alt={`answer-${idx}`} className="rounded-lg border cursor-zoom-in" onClick={() => setSelectedImage(img)} />
                                                    ))}
                                                </div>
                                            )}

                                            {/* Source Information and PDF link */}
                                            {((m.pdfUrl ||
                                                m.sopSource ||
                                                m.sourcedFrom?.documentName) &&
                                                !shouldHideAssistantSourceLine(m.answer || '') &&
                                                m.sourcedFrom?.documentName !== 'Unknown Document') && (
                                                    <div className="mt-2">
                                                        <p className="text-18reg grey-900 mb-2">
                                                            📄&nbsp;
                                                            {m.sopSource ? (
                                                                <span>
                                                                    Sourced from SOP: {m.sopSource.sopName}, {m.sopSource.sectionTitle}
                                                                </span>
                                                            ) : (
                                                                m.sourcedFrom?.documentName && (
                                                                    <span>
                                                                        Sourced from: {m.sourcedFrom.documentName}
                                                                        {m.sourcedFrom.sectionName ? ` — ${m.sourcedFrom.sectionName}` : ''}
                                                                    </span>
                                                                )
                                                            )}
                                                        </p>
                                                        <div className="flex items-center gap-3">
                                                            {(m.sopSource || m.pdfUrl) &&
                                                                m.sourcedFrom?.documentName !== 'Unknown Document' && (
                                                                    <button
                                                                        onClick={() => {
                                                                            const sopSource =
                                                                                m.sopSource || {
                                                                                    sopName:
                                                                                        m.sourcedFrom?.documentName || 'Document',
                                                                                    sectionTitle:
                                                                                        m.sourcedFrom?.sectionName || '',
                                                                                    fileId: 'unknown',
                                                                                    documentContent: undefined,
                                                                                };

                                                                            const url =
                                                                                m.pdfUrl ||
                                                                                (sopSource.fileId &&
                                                                                    sopSource.fileId !== 'unknown'
                                                                                    ? `${API_BASE_URL}/documents/${sopSource.fileId}/pdf`
                                                                                    : undefined);
                                                                            if (!url) return;
                                                                            handleViewSop(sopSource, url);
                                                                        }}
                                                                        className="sop-outline-btn"
                                                                    >
                                                                        → [Tap to View SOP]
                                                                    </button>
                                                                )}
                                                        </div>
                                                    </div>
                                                )}

                                        </div>
                                        <div className="chat-message-footer">
                                            <ChatFeedback
                                                messageId={m.messageId}
                                                sessionId={sessionId}
                                                persistedFeedback={
                                                    m.messageId && messageFeedbackByMessageId[m.messageId]
                                                        ? {
                                                              feedbackType:
                                                                  messageFeedbackByMessageId[m.messageId]
                                                                      .feedbackType,
                                                              reason:
                                                                  messageFeedbackByMessageId[m.messageId]
                                                                      .reason ?? null,
                                                          }
                                                        : undefined
                                                }
                                                onFeedbackCommitted={(payload) => {
                                                    if (!m.messageId) return;
                                                    setMessageFeedbackByMessageId((prev) => ({
                                                        ...prev,
                                                        [m.messageId!]:
                                                            payload.feedbackType === "UP"
                                                                ? { feedbackType: "UP" }
                                                                : {
                                                                      feedbackType: "DOWN",
                                                                      reason: payload.reason,
                                                                  },
                                                    }));
                                                }}
                                            />
                                            <div className="text-13-reg grey-700">AI Assistant [{formatTime(m.timestamp)}]</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            );
                        })}
                        {isLoading && (
                            <div className="ai-thinking">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        )}
                    </div>

                    <div className="ai-chat-input-container">
                        {!isMicOpen ? (
                            <textarea
                                rows={1}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="Ask about procedures, safety protocols, or SOP guidance...."
                                className="ai-chat-input"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleSendMessage();
                                    }
                                }}
                                disabled={isLoading || isLoadingHistory}
                                style={{
                                    resize: 'none',
                                    overflow: 'auto',
                                    height: '45px',
                                    minHeight: '45px',
                                    maxHeight: '65px'
                                }}
                                onInput={(e) => {
                                    const target = e.target as HTMLTextAreaElement;
                                    target.style.height = '45px';
                                    target.style.height = Math.min(target.scrollHeight, 90) + 'px';
                                }}
                            ></textarea>
                        ) : (
                            <div className="ai-chat-mic-pill">
                                <SpeechToText
                                    className="min-w-0 flex-1 h-full flex items-center"
                                    active={true}
                                    height={29}
                                    windowSec={2}
                                    timesliceMs={40}
                                    onTranscript={(text) => {
                                        setSttTranscript(text);
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => {
                                        setIsMicOpen(false);
                                        setSttTranscript("");
                                    }}
                                    className="flex-shrink-0 ml-2 p-1 rounded-full hover:bg-gray-100 text-gray-700"
                                    aria-label="Cancel voice input"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (sttTranscript.trim()) {
                                            setMessage((prev) => (prev ? prev + ' ' : '') + sttTranscript.trim());
                                        }
                                        setSttTranscript("");
                                        setIsMicOpen(false);
                                    }}
                                    className="flex-shrink-0 ml-1 p-1 rounded-full hover:bg-gray-100 text-gray-700"
                                    aria-label="Accept voice input"
                                >
                                    <Check className="w-5 h-5" />
                                </button>
                            </div>
                        )}
                        {!isMicOpen && (
                            <button
                                type="button"
                                onClick={async () => {
                                    if (isLoading || isLoadingHistory) return;
                                    const result = await requestMicPermission();
                                    if (!result.granted) {
                                        toast.error(result.errorMessage);
                                        return;
                                    }
                                    toast.success("Microphone is active. You can start speaking.");
                                    setIsMicOpen(true);
                                }}
                                className="chat-mic-btn"
                                disabled={isLoading || isLoadingHistory}
                                title="Click to use voice input"
                            >
                                <i className="iconoir-microphone-solid"></i>
                            </button>
                        )}
                        <button
                            onClick={handleSendMessage}
                            className="chat-send-btn"
                            disabled={isLoading || isLoadingHistory}
                        >
                            <i className="iconoir-send-solid"></i>
                        </button>
                    </div>
                    </>
                )}
            </div>

            {/* Image Modal */}
            {selectedImage && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setSelectedImage(null)}>
                    <div className="max-w-3xl w-full px-4" onClick={(e) => e.stopPropagation()}>
                        <img src={selectedImage} alt="Selected" className="w-full rounded-lg shadow-lg" />
                    </div>
                </div>
            )}

            {/* SOP Dialog */}
            <SopDialog
                isOpen={sopDialog.isOpen}
                onClose={() => setSopDialog(prev => ({ ...prev, isOpen: false }))}
                sopName={sopDialog.sopName}
                sectionTitle={sopDialog.sectionTitle}
                documentContent={sopDialog.documentContent}
                pdfUrl={sopDialog.pdfUrl}
            />
        </div>
    );
});

// Page component that manages session state
const ManagerChatbotPage: React.FC = () => {
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
    const chatbotRef = useRef<ManagerChatbotHandle>(null);

    const handleMessagesChange = (count: number) => {
        // Message count tracking can be added here if needed
    };

    const handleFirstTurnReady = (messages: ChatSessionMessage[], newSessionId?: string) => {
        if (newSessionId) {
            setCurrentSessionId(newSessionId);
        }
    };

    return (
        <div className="flex-1 min-h-0 flex flex-col min-w-0">
            <ManagerChatbot
                ref={chatbotRef}
                sessionId={currentSessionId}
                onMessagesChange={handleMessagesChange}
                onFirstTurnReady={handleFirstTurnReady}
            />
        </div>
    );
};

export default ManagerChatbotPage;
